import { Content } from '../../therapy/entities/content';
import { CurafidaMode } from '../../user/entities/user-preferences/user-preferences-resource';
import { MeasurementFeatureConfig } from '../../common/entities/curafida-frontend-configuration';

export class ConfigurationResource {
    uuid: string;
    created_at: Date;
    requester: string;
    configuration: ConfigurationDetail;
    logo?: Content;
    backgroundImage?: Content;
    favicon?: Content;
}
export class ConfigurationTheme {
    appName?: string;
    color?: {
        primary: string;
        secondary: string;
    };
}
export class ConfigurationDetail {
    theme?: ConfigurationTheme;
    features: ConfigurationDetailFeatures;
}

/*
 * What's the difference with FeatureConfigs?
 * And why is there CurafidaFeatureEnum and not just keyof ConfigurationDetailFeatures?
 */
export type ConfigurationDetailFeatures = {
    profile?: { enabled: boolean };
    user?: {
        enabled?: boolean;
        patient?: {
            /** Vom Backend verlangt (ConfigurationPatientDto extends ConfigurationBasicDto). */
            enabled?: boolean;
            planer?: { enabled: boolean };
            statistics?: { enabled: boolean };
            defaultMode?: CurafidaMode;
            enabledModus?: CurafidaMode[];
            pseudonymiseOnCreation?: { enabled: boolean };
        };
        caregiver?: {
            enabled: boolean;
        };
        user_manager?: {
            enabled: boolean;
        };
        catalog_manager?: {
            enabled: boolean;
        };
        analyst?: {
            enabled: boolean;
        };
        supervisor?: {
            enabled: boolean;
        };
        knowledge_manager?: {
            enabled: boolean;
        };
    };
    register?: {
        enabled?: boolean;
        registerUrl?: string;
    };
    catalog?: {
        enabled?: boolean;
        showShopSegment?: boolean;
        showStatistics?: boolean;
        shopUrl?: string;
    };
    infoPage?: {
        enabled?: boolean;
    };
    termsAndConditions?: {
        enabled?: boolean;
    };
    chat?: {
        enabled: boolean;
        video?: {
            enabled: boolean;
            tool: string;
            domain: string;
        };
    };
    course?: {
        enabled: boolean;
        disabledAutoActivation?: boolean;
        hasAlternativeAppointment?: boolean;
        hidePatientContent?: boolean;
        hideSegmentCourseForCaregiver?: boolean;
    };
    directus?: {
        enabled: boolean;
        apiBaseUrl: string;
        readOnlyAccessToken: string;
    };
    task?: {
        enabled: boolean;
        allowSkippingTasks?: boolean;
        showMultipleDescription?: boolean;
        bluetoothDeviceMeasurements?: {
            enabled?: boolean;
        };
    };
    program?: {
        enabled: boolean;
        gamification?: { enabled: boolean };
    };
    note?: {
        enabled?: boolean;
    };
    aiAssistant?: {
        enabled: boolean;
        buddy?: {
            enabled: boolean;
            url?: string;
        };
    };
    evaluation?: {
        enabled?: boolean;
    };
    announcement?: {
        enabled?: boolean;
    };
    training?: {
        enabled?: boolean;
    };
    /**
     * Mirrors the backend env `AMBER_GATEWAY_ENABLED`: read-only, derived per response and never sent to patients.
     * Never post it back — the configuration POST rejects it with a 400.
     */
    amberGateway?: {
        enabled: boolean;
    };
};

/**
 * Whether the Auswertungen chart/list should be reachable at all. Chart and list visibility beyond
 * that is decided per field, per role and per display context, and enforced server-side. Kept as a
 * function (not inlined at each call site) so the gate cannot diverge across call sites. Shared by
 * every call site (`patient-mobile`, `patient-minimal-ui-planer`, `home-patient-page`,
 * `patient-dossier`).
 */
export function isEvaluationEnabled(evaluation: ConfigurationDetailFeatures['evaluation']): boolean {
    return !!evaluation?.enabled;
}

/**
 * Whether the vitals chart of the Messungen module should be shown. It is the fallback for tenants
 * without Auswertungen: once the evaluation feature is enabled, Auswertungen takes over and this chart
 * stays hidden, so the two chart systems are never shown side by side. Stays `false` until the backend
 * features are loaded, so the chart cannot flash up before an enabled evaluation feature is known.
 */
export function isMeasurementChartEnabled(
    measurement: MeasurementFeatureConfig | undefined,
    backendFeatures: ConfigurationDetailFeatures | undefined,
): boolean {
    return !!measurement?.enabled && !!backendFeatures && !isEvaluationEnabled(backendFeatures.evaluation);
}

/** A section of the patient's Verlauf tab, each shown as a segment there */
export type PatientHistorySection = 'pastActivities' | 'evaluations' | 'measurements' | 'successes';

/**
 * The Verlauf sections the patient is offered, in segment order. The single source for both the Verlauf tab, which
 * is shown when this is not empty, and its segments, so the tab can neither go missing while it has content nor
 * show up empty.
 */
export function patientHistorySections(
    measurement: MeasurementFeatureConfig | undefined,
    backendFeatures: ConfigurationDetailFeatures | undefined,
): PatientHistorySection[] {
    const sections: PatientHistorySection[] = [];
    if (backendFeatures?.user?.patient?.planer?.enabled) sections.push('pastActivities');
    if (isEvaluationEnabled(backendFeatures?.evaluation)) sections.push('evaluations');
    if (isMeasurementChartEnabled(measurement, backendFeatures)) sections.push('measurements');
    if (backendFeatures?.user?.patient?.statistics?.enabled) sections.push('successes');
    return sections;
}
